import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Chip,
  IconButton,
  InputAdornment,
  Divider,
  LinearProgress,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { useSnackbar } from 'notistack';
import { fetchPost, updatePost, deletePost } from '../services/api';
import { Helmet } from 'react-helmet';

// Blog categories
const CATEGORIES = [
  { name: 'Scholarship', color: '#2196f3' }, // Blue
  { name: 'Education', color: 'green' },   // Orange
  { name: 'Admissions', color: '#9e9e9e' },       // Grey
];

function PostEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    tags: [],
    category: '',
    thumbnailData: null,
    imageData: []
  });

  const [tagInput, setTagInput] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Load post data
  useEffect(() => {
    const loadPost = async () => {
      try {
        const post = await fetchPost(id);
        setFormData({
          title: post.title,
          content: post.content,
          author: post.author,
          tags: post.tags || [],
          category: post.category || '',
          thumbnailData: post.thumbnailData || null,
          imageData: post.imageData || []
        });
      } catch (err) {
        console.error('Error loading post:', err);
        setError('Failed to load post data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleAddTag = () => {
    if (!tagInput.trim()) return;

    // Don't add duplicate tags
    if (formData.tags.includes(tagInput.trim())) {
      setTagInput('');
      return;
    }

    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tagInput.trim()]
    }));
    setTagInput('');
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  // Category handling
  const handleCategoryChange = (event) => {
    setFormData(prev => ({
      ...prev,
      category: event.target.value
    }));

    // Clear error when field is edited
    if (validationErrors.category) {
      setValidationErrors(prev => ({ ...prev, category: '' }));
    }
  };

  // Get category chip color based on category name
  const getCategoryColor = (category) => {
    const foundCategory = CATEGORIES.find(c => c.name === category);
    return foundCategory ? foundCategory.color : '#9e9e9e'; // Default to grey if not found
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    }

    if (!formData.author.trim()) {
      errors.author = 'Author name is required';
    }

    if (!formData.category) {
      errors.category = 'Please select a category';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' });
      return;
    }

    setUpdating(true);
    try {
      await updatePost(id, formData);
      enqueueSnackbar('Post updated successfully', { variant: 'success' });
      navigate('/admin/posts');
    } catch (err) {
      console.error('Error updating post:', err);
      enqueueSnackbar('Failed to update post. Please try again.', { variant: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete this post: "${formData.title}"?`)) {
      try {
        await deletePost(id);
        enqueueSnackbar('Post deleted successfully', { variant: 'success' });
        navigate('/admin/posts');
      } catch (err) {
        console.error('Error deleting post:', err);
        enqueueSnackbar('Failed to delete post', { variant: 'error' });
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="contained"
          onClick={() => navigate('/admin/posts')}
        >
          Back to Posts
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {formData.title ? (
        <Helmet>
          <title>WorldWise Scholar - Edit Post</title>
        </Helmet>
      ) : null}
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
        WorldWise Scholar - Edit Post
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
          aria-label="go back"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500, flexGrow: 1 }}>
          Edit Post
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          sx={{ ml: 2 }}
        >
          Delete
        </Button>
      </Box>

      {updating && <LinearProgress sx={{ mb: 2 }} />}

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                error={!!validationErrors.title}
                helperText={validationErrors.title}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                error={!!validationErrors.author}
                helperText={validationErrors.author}
              />
            </Grid>

            {/* Category Selection */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(validationErrors.category)}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  name="category"
                  value={formData.category}
                  onChange={handleCategoryChange}
                  label="Category"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="span"
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          mr: 1,
                          backgroundColor: getCategoryColor(selected)
                        }}
                      />
                      {selected}
                    </Box>
                  )}
                >
                  {CATEGORIES.map((category) => (
                    <MenuItem
                      key={category.name}
                      value={category.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: category.color,
                          mr: 2
                        }}
                      />
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.category && (
                  <Typography variant="caption" color="error">{validationErrors.category}</Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Add Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                fullWidth
                variant="outlined"
                placeholder="Type tag and press Enter"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleAddTag}
                        edge="end"
                        disabled={!tagInput.trim()}
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap' }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleDeleteTag(tag)}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {formData.tags.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No tags added
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Display Thumbnail if exists */}
            {formData.thumbnailData && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Featured Image (Thumbnail)
                </Typography>
                <Box
                  component="img"
                  src={formData.thumbnailData.data}
                  alt="Post thumbnail"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid #eee'
                  }}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  To change the thumbnail, create a new post with the desired image.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                fullWidth
                required
                multiline
                rows={10}
                variant="outlined"
                error={!!validationErrors.content}
                helperText={validationErrors.content}
              />
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/posts')}
                startIcon={<ClearIcon />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={updating}
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default PostEdit;